import classNames from "classnames";
import { DateTime } from "luxon";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { CartContext } from "../../contexts/CartContext";
import {
  getProductSKUFromTags,
  ProductTagPrefix,
  ProductType,
  SEASON_CARD_ATTRIBUTES,
} from "../../ecommerce/product";
import { CartItem } from "../../types/shopify-components";
import LoadingSpinner from "../LoadingSpinner";

import CartContent from "./CartContent";
import CartSummary from "./CartSummary";

export const TEST_ID_CART_CONTENT_CONTAINER = "cart-content-container";

function onCheckoutClick(checkoutUrl: string): void {
  window.location.assign(checkoutUrl);
}

async function checkSeasonCardImages(items: CartItem[]): Promise<boolean> {
  const imageIds = items.reduce<string[]>((acc, { input, merchandise }) => {
    if (merchandise.productType === ProductType.SEASON_CARD) {
      const imageId = input.attributes?.find(
        ({ key }) => key === SEASON_CARD_ATTRIBUTES.PHOTO_ID,
      )?.value;
      if (imageId) {
        acc.push(imageId);
      }
    }
    return acc;
  }, []);

  if (imageIds.length === 0) {
    return true;
  }

  const results = await Promise.all(
    imageIds.map(id =>
      fetch(
        `${process.env.SARKANNIEMI_API_URI}/v1/ecommerce/season-pass/image/${id}`,
      )
        .then(response => {
          return response.status === 200;
        })
        .catch(() => {
          return false;
        }),
    ),
  );
  return results.every(result => result === true);
}

const Cart: React.FC = () => {
  const { formatMessage } = useIntl();
  const { localItems, isLoading, summaries } = useContext(CartContext);
  const [cartError, setCartError] = useState<string | undefined>(undefined);
  const [isCartValidated, setIsCartValidated] = useState(false);
  const imageCheckRef = useRef<boolean>(false);
  const visibleItems = localItems.filter(
    item =>
      getProductSKUFromTags(item.merchandise.tags)?.type !==
      ProductTagPrefix.COMPOSITE_CHILD_PRODUCT,
  );
  const showSummary = !!visibleItems.length;

  const pastItemIds = useMemo(() => {
    return localItems
      .filter(item => {
        const { validityDate } = item.merchandise;
        if (validityDate === undefined) {
          return false;
        }
        const currentDate = DateTime.local({
          zone: "Europe/Helsinki",
        }).toISODate();
        const variantDate = validityDate.toISODate();
        return currentDate > variantDate;
      })
      .map(item => item.merchandise.id);
  }, [localItems]);

  useEffect(() => {
    if (
      !isLoading &&
      localItems.length > 0 &&
      imageCheckRef.current === false
    ) {
      imageCheckRef.current = true;
      checkSeasonCardImages(localItems).then(imagesFound => {
        if (!imagesFound) {
          setCartError("error.imageExpired");
        }
        setIsCartValidated(true);
      });
    }
  }, [isLoading, localItems]);

  return (
    <section className="flex-ns items-stretch pa2">
      {isLoading ? (
        <div className="bg-white w-100">
          <LoadingSpinner message={formatMessage({ id: "cart.loading" })} />
        </div>
      ) : (
        <>
          <div
            className={classNames("pa2", {
              "w-60-ns w-70-l": showSummary,
              "w-100": !showSummary,
            })}
            data-testid={TEST_ID_CART_CONTENT_CONTAINER}
          >
            <CartContent
              items={localItems}
              pastItemIds={pastItemIds}
              summary={summaries.local}
            />
          </div>
          {showSummary && (
            <aside className="w-40-ns w-30-l pa2">
              <CartSummary
                checkoutFn={onCheckoutClick}
                error={cartError}
                hasPastItems={pastItemIds.length > 0}
                isCartValidated={isCartValidated}
              />
            </aside>
          )}
        </>
      )}
    </section>
  );
};

export default Cart;
